import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { CheckCircle, Shield, Settings, Zap, Users, Award, Phone, Mail, Calendar, ArrowRight } from "lucide-react";
import React from 'react';
import "../styles/template.css"; // Updated path to styles directory

const InsuranceServices = () => {
  const coreCapabilities = [
    {
      icon: Settings,
      title: "Benefit & Provider Configuration",
      description: [
        "Configuration of fee schedules, diagnosis codes (ICD/CPT/DRG), and reimbursement rules",
        "Network contract loading, provider/practitioner setup, and value-based care logic implementation",
        "Configuration audit and maintenance across PIX, Emptoris/Icertis, and ServiceNow environments"
      ],
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      icon: Shield,
      title: "Pricing & Contract Management",
      description: [
        "Contract interpretation and transformation into system-ready configurations",
        "Integration of pricing rules with testing workflows for CMS1500 and UB04 claims",
        "Strong focus on Medicare, Medicaid, and commercial plan compliance"
      ],
      gradient: "from-purple-500 to-pink-500"
    },
    {
      icon: Zap,
      title: "Claims Testing & Auto-Adjudication",
      description: [
        "Comprehensive QA/UAT for claim workflows with regression testing",
        "Scenario-based testing to validate configuration alignment in Amisys/DST and Facets",
        "Automated test cases generation for clean adjudication pipelines"
      ],
      gradient: "from-orange-500 to-red-500"
    },
    {
      icon: Users,
      title: "Workflow Automation & Integration",
      description: [
        "SQL-based data extraction, validation, and reconciliation across dev/test/prod environments",
        "Configuration lifecycle management with CIA workflow (Change Impact Assessment)",
        "Seamless migration between environments with post-production support"
      ],
      gradient: "from-green-500 to-teal-500"
    }
  ];

  const technologies = [
    { category: "Systems", items: ["Amisys", "DST", "Facets", "QNXT", "Aldera", "HRP"], color: "bg-blue-50 border-blue-200 text-blue-800" },
    { category: "Testing", items: ["CMS 1500 / UB04 Claims", "UAT", "Regression"], color: "bg-purple-50 border-purple-200 text-purple-800" },
    { category: "Automation", items: ["SQL Server", "ServiceNow", "SharePoint", "Emptoris/Icertis"], color: "bg-green-50 border-green-200 text-green-800" },
    { category: "Methodologies", items: ["Agile", "Scrum", "SDLC"], color: "bg-orange-50 border-orange-200 text-orange-800" }
  ];

  const whyChooseUs = [
    { text: "100% audit-aligned benefit configurations", icon: CheckCircle },
    { text: "End-to-end ownership from requirement gathering to PROD validation", icon: Settings },
    { text: "Predictable claims processing and financial integrity", icon: Shield },
    { text: "Flexibility with onsite, hybrid, and offshore support models", icon: Users },
    { text: "Compliance-oriented approach for federal and commercial plans", icon: Award }
  ];

  const expertiseAreas = [
    { title: "Configuration Analysts", count: "15+" },
    { title: "Pricing & Benefit Specialists", count: "12+" },
    { title: "Contract BAs", count: "8+" },
    { title: "UAT/Test Engineers", count: "20+" },
    { title: "Project Managers", count: "6+" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white overflow-x-hidden">
      {/* Enhanced Hero Section with Image */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden py-16 md:py-20">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/Images/Insurance.jpg" 
            alt="Insurance protection concept"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-blue-800/80 to-transparent"></div>
        </div>
        
        {/* Animated floating elements */}
        <div className="absolute inset-0 overflow-hidden z-10">
          <div className="absolute top-20 left-20 w-20 h-20 bg-white/10 rounded-full animate-pulse"></div>
          <div className="absolute top-40 right-32 w-16 h-16 bg-teal-300/20 rounded-full animate-bounce"></div>
          <div className="absolute bottom-32 left-1/4 w-12 h-12 bg-blue-300/30 rounded-full animate-ping"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-20 max-w-7xl mx-auto px-4 text-center text-white">
          <div className="animate-fade-in px-4">
            <div className="mb-4 md:mb-6">
              <span className="text-5xl md:text-6xl animate-bounce">🛡</span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold mb-4 md:mb-6 leading-tight animate-slide-in-right">
              Insurance Services by <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-300 to-blue-300">Digitechub</span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl mb-8 md:mb-12 text-blue-100 max-w-4xl mx-auto leading-relaxed animate-fade-in">
              Transforming Healthcare Payer Solutions with Precision & Performance
            </p>
            <Button size="lg" className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white px-8 py-4 text-base md:px-10 md:py-6 md:text-xl font-semibold rounded-full transition-all duration-300 transform hover:scale-110 shadow-2xl group">
              Get Free Consultation
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/70 rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-12 md:py-20 px-4 relative">
        <div className="max-w-6xl mx-auto text-center">
          <div className="animate-fade-in">
            <p className="text-base md:text-xl text-gray-700 max-w-5xl mx-auto leading-relaxed mb-8 md:mb-12 font-light">
              At Digitechub, we bring over a decade of expertise in US Healthcare Insurance Systems with 
              specialization in payer-side configuration, benefit administration, claims testing, and system 
              integrations. Our experienced professionals deliver scalable solutions that power seamless 
              operations for healthcare providers, payers, and TPAs across platforms like Amisys, DST, 
              Facets, Aldera, and QNXT.
            </p>
          </div>
        </div>
      </section>

      {/* Enhanced Core Capabilities Section */}
      <section className="py-12 md:py-20 px-4 bg-gradient-to-br from-gray-50 to-blue-50/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-16 animate-fade-in">
            <span className="text-4xl md:text-5xl mb-4 md:mb-6 block">🔧</span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 md:mb-6">
              Core Capabilities
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive solutions for healthcare insurance systems
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {coreCapabilities.map((capability, index) => (
              <Card key={index} className="group hover:shadow-2xl transition-all duration-500 border-0 shadow-lg hover:-translate-y-4 bg-white/80 backdrop-blur-sm overflow-hidden">
                <div className={`absolute inset-0 bg-gradient-to-br ${capability.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>
                <CardHeader className="pb-3 md:pb-4 relative z-10">
                  <div className={`w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br ${capability.gradient} rounded-2xl flex items-center justify-center mb-4 md:mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg`}>
                    <capability.icon className="h-8 w-8 md:h-10 md:w-10 text-white" />
                  </div>
                  <CardTitle className="text-xl md:text-2xl font-bold text-gray-900 group-hover:text-blue-800 transition-colors">
                    {capability.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="relative z-10">
                  <ul className="space-y-3 md:space-y-4">
                    {capability.description.map((item, idx) => (
                      <li key={idx} className="flex items-start group/item text-sm md:text-base">
                        <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-teal-500 mt-1 mr-3 md:mr-4 flex-shrink-0 group-hover/item:scale-110 transition-transform" />
                        <span className="text-gray-700 leading-relaxed group-hover/item:text-gray-900 transition-colors">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Our Experts Section */}
      <section className="py-12 md:py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 md:mb-16">
            <span className="text-4xl md:text-5xl mb-4 md:mb-6 block">👨‍💼</span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 md:mb-8">
              Our Experts
            </h2>
            <p className="text-lg md:text-xl text-gray-700 mb-8 md:mb-12 max-w-4xl mx-auto">
              With specialists holding 10–17+ years of experience from industry leaders like NTT Data, 
              Wipro, and Cognizant, our team has worked on projects for Fortune 500 healthcare providers 
              and MCOs (Managed Care Organizations).
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-12 md:mb-16">
            {["NTT Data", "Wipro", "Cognizant"].map((company, index) => (
              <div key={company} className="group bg-white p-6 md:p-8 rounded-2xl shadow-lg border hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                <div className="text-center">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4 group-hover:scale-110 transition-transform">
                    <Award className="h-7 w-7 md:h-8 md:w-8 text-white" />
                  </div>
                  <h3 className="text-lg md:text-xl font-semibold text-gray-800 mb-1 md:mb-2">Partner Experience</h3>
                  <p className="text-xl md:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">{company}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {expertiseAreas.map((area, index) => (
              <div key={index} className="group bg-gradient-to-r from-blue-50 to-teal-50 p-6 md:p-8 rounded-2xl border border-blue-100 text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <div className="text-2xl md:text-3xl font-bold text-blue-600 mb-1 md:mb-2 group-hover:scale-110 transition-transform">{area.count}</div>
                <p className="font-semibold text-gray-800 text-base md:text-lg">{area.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Tools & Technologies Section */}
      <section className="py-12 md:py-20 px-4 bg-gradient-to-br from-gray-50 to-blue-50/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 md:mb-16">
            <span className="text-4xl md:text-5xl mb-4 md:mb-6 block">⚙</span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-8 md:mb-12">
              Tools & Technologies
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {technologies.map((tech, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white/80 backdrop-blur-sm">
                <CardHeader className="text-center pb-3 md:pb-4">
                  <CardTitle className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4">
                    {tech.category}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 md:space-y-3">
                    {tech.items.map((item, idx) => (
                      <div key={idx} className={`${tech.color} px-3 py-1 md:px-4 md:py-2 rounded-lg border text-center font-medium transition-all duration-200 hover:scale-105 cursor-default text-sm md:text-base`}>
                        {item}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Why Choose Us Section */}
      <section className="py-12 md:py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 md:mb-16">
            <span className="text-4xl md:text-5xl mb-4 md:mb-6 block">⭐</span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Choose Us
            </h2>
            <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
              Our commitment to excellence and client success sets us apart.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {whyChooseUs.map((reason, index) => (
              <div key={index} className="group bg-white p-6 md:p-8 rounded-2xl shadow-lg border hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 flex items-start space-x-4">
                <reason.icon className="h-6 w-6 md:h-8 md:w-8 text-blue-500 flex-shrink-0 mt-1 group-hover:scale-110 transition-transform" />
                <p className="text-gray-700 text-base md:text-lg leading-relaxed group-hover:text-gray-900 transition-colors">{reason.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 md:py-20 px-4 bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6 md:mb-8">
            Let's Connect
          </h2>
          <p className="text-lg md:text-xl mb-8 md:mb-12 max-w-3xl mx-auto">
            Ready to optimize your healthcare insurance operations? Contact us today for a free consultation.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 md:gap-6">
            <Button size="lg" className="bg-white text-blue-700 hover:bg-blue-100 px-8 py-4 text-base md:px-10 md:py-6 md:text-xl font-semibold rounded-full transition-all duration-300 transform hover:scale-105 shadow-2xl group">
              <Phone className="mr-2 h-5 w-5" /> Call Us
            </Button>
            <Button size="lg" className="bg-blue-700 text-white hover:bg-blue-800 px-8 py-4 text-base md:px-10 md:py-6 md:text-xl font-semibold rounded-full transition-all duration-300 transform hover:scale-105 shadow-2xl group">
              <Mail className="mr-2 h-5 w-5" /> Email Us
            </Button>
            <Button size="lg" className="bg-white text-blue-700 hover:bg-blue-100 px-8 py-4 text-base md:px-10 md:py-6 md:text-xl font-semibold rounded-full transition-all duration-300 transform hover:scale-105 shadow-2xl group">
              <Calendar className="mr-2 h-5 w-5" /> Schedule a Demo
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InsuranceServices; 